'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function DusukGozKapagindaBotoksKullanimiPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/dusuk-goz-kapaginda-botoks-kullanimi';
  const pageTitle = 'Düşük Göz Kapağında Botoks Kullanımı';
  const pageDescription = 'Göz kapağı düşüklüğü botoks ile tedavi edilebilir mi? Düşük kaş ve üst göz kapağı sarkmalarında botoksun etkisi, uygulanma alanları ve dikkat edilmesi gerekenler.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Düşük Göz Kapağında Botoks Kullanımı', href: '/tr/blog/dusuk-goz-kapaginda-botoks-kullanimi' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Düşük göz kapağında botoks kullanımı ve ptozis tedavisi değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Ptozis Tedavisi
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Düşük Göz Kapağında Botoks Kullanımı
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>7 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>6 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Göz kapağı düşüklüğü (ptozis), yüz ifadesini yorgun, üzgün ve yaşlı gösteren bir durumdur. Kimi zaman doğuştan, kimi zaman yaşlanma, kas zayıflığı veya yanlış kas aktivitesi sonucu ortaya çıkar.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Günümüzde botoks, bazı tip göz kapağı düşüklüklerinde cerrahiye gerek kalmadan göz çevresini kaldırmak ve daha canlı bir ifade kazandırmak amacıyla kullanılmaktadır.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Ancak her göz kapağı düşüklüğü botoksla düzeltilemez — doğru teknik ve uygun hasta seçimi çok önemlidir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Göz Kapağı Düşüklüğü (Ptozis) Nedir?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Ptozis, üst göz kapağının normalden aşağıda konumlanmasıdır. Kişinin görüş alanını daraltabilir, ayrıca yüz ifadesinde yorgun bir görünüm oluşturur.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Nedenleri:
              </p>

              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-red-800 space-y-2">
                  <li>Kas güçsüzlüğü (levator kası zayıflığı)</li>
                  <li>Cilt elastikiyet kaybı</li>
                  <li>Yaşlanma veya yerçekimi etkisi</li>
                  <li>Botoksun yanlış uygulanması</li>
                  <li>Nörolojik hastalıklar (nadir)</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Botoks Göz Kapağını Nasıl Etkiler?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks, kasları gevşeterek istenmeyen kas kasılmalarını azaltır. Doğru noktaya uygulandığında, kaşların ve üst göz kapağının hafifçe yukarı kalkmasını sağlar.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-blue-800 font-medium">
                  💡 Amaç: Aşağı çeken kasların (orbicularis oculi) etkisini azaltmak → yukarı kaldırıcı kasların (frontalis) daha aktif çalışmasına izin vermek.
                </p>
                <p className="text-blue-800 mt-2">
                  Bu sayede göz çevresi daha açık, dinlenmiş ve genç bir görünüm kazanır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Uygulama Bölgeleri
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Bölge
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Etki
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Açıklama
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kaş dış kısmı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kaşın hafif kalkmasını sağlar</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Göz kapağını yukarı çeker</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Göz çevresi (crow's feet)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kas gevşemesiyle lifting etkisi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Daha canlı bakışlar</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Alın bölgesi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kaş düşüklüğünü engeller</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Göz kapağının baskısını azaltır</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6 font-medium">
                Doğru teknikle uygulandığında botoks, göz kapağı düşüklüğünü hafifletir ve bakışları açar.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Botoksun Etkili Olduğu Durumlar
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks yalnızca kas kaynaklı hafif veya orta düzey göz kapağı düşüklüğünde etkilidir.
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-green-900 mb-3">✅ Etkili Durumlar:</h3>
                  <ul className="list-disc list-inside text-green-800 space-y-2">
                    <li>Kaşın düşük olması nedeniyle kapak sarkıyorsa</li>
                    <li>Göz çevresinde aşırı kas aktivitesi varsa</li>
                    <li>Cerrahiye gerek duyulmayan estetik şikayetlerde</li>
                  </ul>
                </div>

                <div className="bg-red-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-red-900 mb-3">⚠️ Etkisiz Durumlar:</h3>
                  <ul className="list-disc list-inside text-red-800 space-y-2">
                    <li>Kas veya sinir hasarına bağlı ptozis</li>
                    <li>Fazla deri sarkması (dermatoşalazis)</li>
                    <li>Göz kapağında yapısal deformite</li>
                  </ul>
                </div>
              </div>

              <p className="text-gray-700 mb-6 font-medium">
                Bu tür durumlarda cerrahi blefaroplasti veya levator kas onarımı gerekebilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. İşlem Nasıl Yapılır?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Göz çevresi temizlenir.
              </p>

              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <ol className="list-decimal list-inside text-purple-800 space-y-2">
                  <li>Göz çevresi temizlenir</li>
                  <li>Çok ince iğnelerle kaş dışı ve alın bölgesine mikro dozlarda botoks uygulanır</li>
                  <li>İşlem yaklaşık 10 dakika sürer</li>
                  <li>Etki 3–5 gün içinde başlar, maksimum etki 10–14. günde ortaya çıkar</li>
                </ol>
                <p className="text-purple-800 mt-3 font-medium">
                  💎 Etki süresi: 4–6 ay
                </p>
                <p className="text-purple-800 font-medium">
                  İşlem sonrası hasta hemen sosyal yaşamına dönebilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Olası Yan Etkiler ve Önlemler
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Geçici şişlik, hafif kızarıklık
              </p>

              <div className="bg-orange-50 rounded-xl p-6 mb-6">
                <p className="text-orange-800 font-medium">
                  Çok nadiren ters etkiyle geçici ptozis (göz kapağı daha da düşmesi)
                </p>
                <p className="text-orange-800 mt-2">
                  → Bu durumda apraclonidine damla geçici çözüm sağlayabilir.
                </p>
                <p className="text-orange-800 mt-3 font-medium">
                  💡 Bu nedenle göz kapağı çevresine botoks yalnızca deneyimli hekim tarafından uygulanmalıdır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. Botoksun Avantajları
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>✅ Cerrahi müdahale gerektirmez</li>
                  <li>✅ Hızlı ve konforlu işlem</li>
                  <li>✅ Göz çevresinde lifting etkisi</li>
                  <li>✅ Doğal ve dinamik yüz ifadesi</li>
                  <li>✅ Kısa sürede iyileşme</li>
                </ul>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Düşük göz kapağında botoks kullanımı ve ptozis tedavisi"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Göz kapağı düşüklüğü botoksla tamamen geçer mi?</h3>
                  <p className="text-gray-700">Hayır. Hafif düzeydeki düşüklüklerde iyileşme sağlar; ileri vakalarda cerrahi gerekir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Etkisi ne kadar sürer?</h3>
                  <p className="text-gray-700">Ortalama 4–6 ay kalıcıdır, düzenli uygulamalarda etki süresi uzar.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Botoks göz kapağını düşürür mü?</h3>
                  <p className="text-gray-700">Yanlış noktaya veya fazla dozda uygulanırsa evet, geçici ptozis gelişebilir. Bu nedenle deneyimli doktorlarca yapılmalıdır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Kaş kaldırma ile birlikte uygulanabilir mi?</h3>
                  <p className="text-gray-700">Evet. Kombine uygulamalarda daha belirgin lifting etkisi elde edilir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Botoks sonrası neye dikkat etmeliyim?</h3>
                  <p className="text-gray-700">İlk 4 saat eğilmemek, masaj yapmamak, uyumamak gerekir. İlk 24 saat makyaj ve sauna önerilmez.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Academy of Ophthalmology (AAO) – "Botulinum Toxin in Eyelid and Brow Ptosis"</li>
                <li>Türk Oftalmoloji Derneği (TOD) – "Blefaroplasti ve Botoks Rehberi"</li>
                <li>Mayo Clinic – "Botox for Eyelid and Brow Lift"</li>
                <li>Harvard Health Publishing – "Non-surgical Eye Rejuvenation Techniques"</li>
                <li>ISAPS Global Report 2024 – "Botulinum Toxin in Periorbital Rejuvenation"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#gözkapağıdüşüklüğü</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#ptozis</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#kaşkaldırma</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#gözçevresi</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#estetik</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Göz Kapağı Düşüklüğü İçin Randevu Alın</h3>
              <p className="mb-6">Düşük göz kapağı ve ptozis tedavisi için uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Göz kapağı düşüklüğü botoks tedavisi hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-amber-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/kaz-ayagi-botoksu-nedir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Kaz Ayağı Botoksu Nedir?</h4>
              <p className="text-gray-600 text-sm">Göz çevresi botoksu ile kaz ayağı kırışıklıklarını ortadan kaldırmak.</p>
            </Link>
            <Link
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir ve Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoksun ne olduğu, nasıl çalıştığı ve uygulama sürecinin ayrıntılı anlatımı.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
